/**
 * KpiCard — één KPI: label, waarde, QoQ/YoY-badges en een mini-sparkline.
 * Richting-bewust: bij lowerIsBetter (klachten, refunds) kleurt dalen groen.
 * Klik selecteert de metric voor de grote trendgrafiek eronder.
 */

import { useEffect, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import {
  formatMetricValue,
  isRunningPeriod,
  useCijfersStore,
  type OverviewMetric,
  type SeriesPoint,
} from '../../stores/cijfers-store';

const nlPct = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function DeltaBadge({ label, delta, lowerIsBetter }: { label: string; delta: number | null; lowerIsBetter: boolean }) {
  if (delta === null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        {label} —
      </span>
    );
  }
  const good = lowerIsBetter ? delta < 0 : delta > 0;
  const neutral = Math.abs(delta) < 0.001;
  const color = neutral ? 'text-gray-500 bg-gray-100' : good ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50';
  const arrow = neutral ? '' : delta > 0 ? '▲' : '▼';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${color}`}>
      <span className="text-gray-500">{label}</span>
      {arrow} {nlPct.format(Math.abs(delta) * 100)}%
    </span>
  );
}

/** Dimensies opsommen tot één waarde per periode voor de sparkline. */
function sumByPeriod(points: SeriesPoint[]): Array<{ period: string; value: number }> {
  const map = new Map<string, number>();
  for (const p of points) {
    if (p.value === null) continue;
    map.set(p.period, (map.get(p.period) ?? 0) + p.value);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([period, value]) => ({ period, value }));
}

interface KpiCardProps {
  metric: OverviewMetric;
  selected: boolean;
  onSelect: (key: string) => void;
}

export function KpiCard({ metric, selected, onSelect }: KpiCardProps) {
  const loadSeries = useCijfersStore((s) => s.loadSeries);
  const grain = useCijfersStore((s) => s.grain);
  const [spark, setSpark] = useState<Array<{ period: string; value: number }>>([]);

  useEffect(() => {
    let active = true;
    void loadSeries(metric.key).then((series) => {
      if (!active || !series) return;
      // Lopende periode uit de sparkline: een half kwartaal leest als crash.
      let points = sumByPeriod(series.points);
      const last = points[points.length - 1];
      if (points.length > 2 && last && isRunningPeriod(last.period, grain)) {
        points = points.slice(0, -1);
      }
      setSpark(points.slice(-8));
    });
    return () => {
      active = false;
    };
  }, [metric.key, grain, loadSeries]);

  return (
    <button
      onClick={() => onSelect(metric.key)}
      className={`text-left p-4 rounded-xl border bg-white transition-all hover:shadow-sm ${
        selected ? 'border-accent ring-1 ring-accent' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-gray-500 truncate">{metric.label}</p>
          <p className="text-xl font-semibold text-main mt-1 tabular-nums">
            {formatMetricValue(metric.value, metric.format)}
          </p>
        </div>
        {spark.length > 1 && (
          <div className="w-20 h-9 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="var(--brand-accent, #4f46e5)"
                  strokeWidth={1.5}
                  fill="var(--brand-accent, #4f46e5)"
                  fillOpacity={0.08}
                  isAnimationActive={false}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <DeltaBadge label="t.o.v. vorige" delta={metric.qoq_pct} lowerIsBetter={metric.lowerIsBetter} />
        <DeltaBadge label="YoY" delta={metric.yoy_pct} lowerIsBetter={metric.lowerIsBetter} />
      </div>
    </button>
  );
}
